import type { LaunchIdentityRecord } from '../../shared/types/launch-identity';
import type { MissionProgressSnapshot } from '../../shared/types/mission-progress';
import type { GameProgressSnapshot } from '../../shared/types/progress';
import type { ChildProfile, FamilyProfile } from '../../shared/types/profiles';
import type { GameSessionSummary, SessionRecord } from '../../shared/types/session';
import type { SkillEvidence } from '../../shared/types/skills';
import type { PayloadProjector } from './json-store';
import { progressKey, STORAGE_KEYS } from './keys';
import {
  toChildProfileListDto,
  toFamilyProfileDto,
  toGameProgressDto,
  toKeyedDto,
  toLaunchIdentityDto,
  toMissionProgressDto,
  toSessionRecordListDto,
  toSessionSummaryDto,
  toSkillEvidenceListDto,
} from './persistence-dto';

/**
 * The one table of "which key holds what", shared by the repositories and the decontamination
 * pass.
 *
 * The pass (review round 5 SC-2, round 6 SC-4) validates every governed key at startup through
 * *the same* projector the owning repository uses, so a contaminated or rejected record is
 * destroyed on the launch after it appeared rather than on the launch that happens to read that
 * key. That is only true if there is exactly one definition of each projector — a second copy in
 * the pass would drift, and the drift would show up as a record the pass waves through and the
 * repository rejects. So the repositories import theirs from here too.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The migration layer's own bookkeeping: the schema version storage was last brought up to. */
export interface StorageMeta {
  schemaVersion: number;
}

/**
 * The metadata key is governed like every other one (review R6-5).
 *
 * It used to be the single exception — projected only in memory by the caller, skipped by the
 * sweep, and therefore the one key whose failed clean rewrite had no erasure fallback. Nothing
 * about it justifies that: it is a one-field record, and bytes hiding beside that field are
 * exactly as forbidden as bytes anywhere else. What the exception was really protecting is the
 * *envelope* version, and that is handled where it belongs — the pass leaves any supported older
 * envelope alone, and `createLocalRepositories` still reads this key through `readEnvelope` so a
 * downgrade cannot make the migration runner think it is starting from nothing.
 */
export const projectStorageMeta: PayloadProjector<StorageMeta> = (data) => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const version = (data as { schemaVersion?: unknown }).schemaVersion;
  return typeof version === 'number' && Number.isInteger(version) && version >= 0
    ? { schemaVersion: version }
    : null;
};

export const projectChildren: PayloadProjector<ChildProfile[]> = (data) =>
  Array.isArray(data) ? toChildProfileListDto(data) : null;

export const projectFamily: PayloadProjector<FamilyProfile> = (data) => toFamilyProfileDto(data);

/**
 * Progress, filed under `<subject>::<game>` — and only when the row says so itself (S1-6).
 *
 * The composite key is the only thing `getGameProgress` matches on, so a row whose own `playerId`
 * or `gameId` disagrees with the key it sits under is a row that would be served to the wrong
 * child, or as the wrong game's progress. Both fields are already in the record; nothing checked
 * that they agreed. Dropped rather than repaired: which of the two claims is the true one is
 * exactly what damage has made unknowable, and the cost of dropping is one re-earned progression
 * record (D-044(3) — degrade downward).
 */
export const projectProgressTable: PayloadProjector<Record<string, GameProgressSnapshot>> = (
  data,
) =>
  isPlainObject(data)
    ? toKeyedDto(data, (entry, key) => {
        const projected = toGameProgressDto(entry);
        if (projected === null) return null;
        return progressKey(projected.playerId, projected.gameId) === key ? projected : null;
      })
    : null;

export const projectSessions: PayloadProjector<SessionRecord[]> = (data) =>
  Array.isArray(data) ? toSessionRecordListDto(data) : null;

export const projectSessionSummaries: PayloadProjector<Record<string, GameSessionSummary>> = (
  data,
) => (isPlainObject(data) ? toKeyedDto(data, toSessionSummaryDto) : null);

export const projectMissionProgress: PayloadProjector<MissionProgressSnapshot> = (data) =>
  toMissionProgressDto(data);

export const projectLaunchIdentity: PayloadProjector<LaunchIdentityRecord> = (data) =>
  toLaunchIdentityDto(data);

/**
 * Evidence, filed under the learning subject each row names (S1-6).
 *
 * An evidence row is the closest thing Thrive keeps to a record of a child, and `playerId` on the
 * row is that child (D-052(3)). A row filed under a different key would be read back as somebody
 * else's learning by `getSkillEstimates` — the one boundary the whole `learningSubjectIdOf` seam
 * exists to hold. Rows that disagree with their key are dropped; the rest of the list survives,
 * because one damaged row is not a reason to take a child's history away.
 */
export const projectSkillEvidence: PayloadProjector<Record<string, SkillEvidence[]>> = (data) =>
  isPlainObject(data)
    ? toKeyedDto(data, (entry, key) =>
        Array.isArray(entry)
          ? toSkillEvidenceListDto(entry).filter((row) => row.playerId === key)
          : null,
      )
    : null;

/**
 * A governed key this build has no reader for.
 *
 * Skill estimates are derived from evidence and never stored (D-025), so the key is reserved and
 * nothing writes it. Bytes under a reserved key came from somewhere this build cannot vouch for
 * and nothing will ever read them, which is precisely the situation the sweep exists to end:
 * rejecting them means they are quarantined and physically erased at launch.
 */
export const projectNothing: PayloadProjector<unknown> = () => null;

export interface GovernedRecord {
  key: string;
  project: PayloadProjector<unknown>;
}

/**
 * Every namespaced key this build recognises, with the projector that validates it.
 *
 * This table is also the **namespace allowlist**. The decontamination pass owns `thrive.v1.*`
 * fail-closed: a key that is not here and is not a valid quarantine diagnostic has no legitimate
 * writer, so its bytes are erased at launch. A future build that starts persisting something new
 * — including a migration that seeds a key — must register it here, or its data will be
 * destroyed on the launch that writes it. That is the deliberate direction to fail in: unknown
 * bytes under our own namespace are indistinguishable from bytes we must not keep.
 */
export const GOVERNED_RECORDS: readonly GovernedRecord[] = [
  { key: STORAGE_KEYS.meta, project: projectStorageMeta },
  { key: STORAGE_KEYS.family, project: projectFamily },
  { key: STORAGE_KEYS.children, project: projectChildren },
  { key: STORAGE_KEYS.progress, project: projectProgressTable },
  { key: STORAGE_KEYS.sessions, project: projectSessions },
  { key: STORAGE_KEYS.sessionSummaries, project: projectSessionSummaries },
  { key: STORAGE_KEYS.skillEvidence, project: projectSkillEvidence },
  { key: STORAGE_KEYS.missionProgress, project: projectMissionProgress },
  { key: STORAGE_KEYS.launchIdentity, project: projectLaunchIdentity },
  { key: STORAGE_KEYS.skillEstimates, project: projectNothing },
];
